import { Option } from "@jutro/types";
import { AddCircleIcon, Button } from "@jutro/ui";
import { Dayjs } from "dayjs";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { StaffDetailsActionModal } from "./components/Modals/StaffDetailsActionModal";
import { SetContainerRef } from "components/AnimatedHeaderOnScrollWrapper/types";
import { ConfirmDialog } from "components/new/ConfirmDialog";
import {
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmNonWorkingPeriod,
} from "lib/graphql/megaSchema";
import {
  staffScheduleSearchFilterAtom,
  staffScheduleStaffGenreFilterAtom,
  staffScheduleStartDateFilterAtom,
} from "views/StaffSchedule/StaffScheduleCreator/atoms";
import { AllVisitsWithDeletedShiftsButton } from "views/StaffSchedule/StaffScheduleCreator/components/AllVisitsWithDeletedShiftsButton";
import { BlockActionModal } from "views/StaffSchedule/StaffScheduleCreator/components/Modals/index";
import { Schedule } from "views/StaffSchedule/StaffScheduleCreator/components/Schedule";
import { useScheduleCreator } from "views/StaffSchedule/StaffScheduleCreator/hooks/useScheduleCreator";
import {
  SlotActionModalMode,
  StaffOption,
  generateDaysInCurrentMonthRow,
  isMedicalAppShift,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import { StaffScheduleDataTestId } from "views/StaffSchedule/StaffScheduleDataTestId";
import { staffScheduleSelectedOrganizationIdAtom } from "views/StaffSchedule/atoms";
import { StaffScheduleFilters } from "views/StaffSchedule/components/StaffScheduleFilters";

type Props = {
  isScrolled: boolean;
  setContainerRef: SetContainerRef;
};

export const StaffScheduleCreator = ({
  isScrolled,
  setContainerRef,
}: Props) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useAtom(
    staffScheduleSelectedOrganizationIdAtom,
  );
  const [startDate, setStartDate] = useAtom(staffScheduleStartDateFilterAtom);
  const [selectedStaffGenre, setSelectedStaffGenre] = useAtom(
    staffScheduleStaffGenreFilterAtom,
  );
  const [searchInputValue, setSearchInputValue] = useAtom(
    staffScheduleSearchFilterAtom,
  );

  const [modalMode, setModalMode] = useState<SlotActionModalMode>("create");
  const [isBlockActionModalOpen, setIsBlockActionModalOpen] =
    useState<boolean>(false);
  const [isStaffDetailsActionModalOpen, setIsStaffDetailsActionModalOpen] =
    useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  const [selectedBlockStaffId, setSelectedBlockStaffId] = useState<string>("");
  const [selectedStaffDetails, setSelectedStaffDetails] =
    useState<Option<StaffOption> | null>(null);
  const [selectedDay, setSelectedDay] = useState<Dayjs | undefined>(undefined);
  const [selectedBlock, setSelectedBlock] = useState<
    EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod | undefined
  >(undefined);

  const days = useMemo(
    () => generateDaysInCurrentMonthRow(startDate, startDate.endOf("month")),
    [startDate],
  );

  const {
    filteredMedicalStaff,
    medicalStaffSelectOptions,
    shifts,
    publishShiftsLoading,
    handlePublishShifts,
  } = useScheduleCreator({
    from: startDate,
    searchInput: searchInputValue,
    selectedOrganizationId,
    selectedStaffGenre,
  });

  const { data: shiftsData, loading: shiftsLoading } = shifts;

  const isShiftDataDirty = shiftsData.some((o) => {
    if (isMedicalAppShift(o)) {
      return (
        (o.state === "DRAFT" || o.state === "PRE_DELETED") &&
        o.organizationId === selectedOrganizationId
      );
    }

    return false;
  });

  const handleBlockActionModalOpen = (
    mode: SlotActionModalMode,
    day?: Dayjs,
  ) => {
    setIsBlockActionModalOpen(true);
    setSelectedDay(day);
    setModalMode(mode);
  };

  const handleCloseBlockActionModal = () => {
    setIsBlockActionModalOpen(false);
    setSelectedBlockStaffId("");
    setSelectedDay(undefined);
    setSelectedBlock(undefined);
  };

  const handleStaffDetailsActionModalOpen = (
    staff: Option<StaffOption> | null,
  ) => {
    setIsStaffDetailsActionModalOpen(true);
    setSelectedStaffDetails(staff);
  };

  const handleCloseStaffDetailsActionModal = () => {
    setIsStaffDetailsActionModalOpen(false);
    setSelectedStaffDetails(null);
  };

  return (
    <>
      <div className="font-paragraph-2 flex h-full flex-col gap-2 overflow-auto">
        <div className="absolute top-3 right-3 flex justify-end gap-4">
          <AllVisitsWithDeletedShiftsButton />
          <div>
            <Button
              data-testId={StaffScheduleDataTestId.CreateShiftButton}
              text="Utwórz zmianę"
              variant="secondary"
              iconPosition="left"
              icon={<AddCircleIcon />}
              onClick={() => handleBlockActionModalOpen("create")}
              size="condensed"
            />
          </div>
          <div>
            <Button
              data-testId={StaffScheduleDataTestId.PublishShiftsButton}
              text="Opublikuj"
              onClick={() => setIsConfirmationModalOpen(true)}
              loading={publishShiftsLoading}
              disabled={!shiftsData.length || !isShiftDataDirty}
              size="condensed"
            />
          </div>
        </div>

        <StaffScheduleFilters
          isScrolled={isScrolled}
          startDate={startDate}
          selectedOrganizationId={selectedOrganizationId}
          selectedStaffGenre={selectedStaffGenre}
          searchInputValue={searchInputValue}
          setStartDate={setStartDate}
          setSelectedOrganizationId={setSelectedOrganizationId}
          setSelectedStaffGenre={setSelectedStaffGenre}
          setSearchInputValue={setSearchInputValue}
        />

        {shiftsLoading ? (
          <div className="bg-jutro-new-warm-gray-50 w-full flex-1 animate-pulse rounded-2xl duration-300" />
        ) : (
          <Schedule
            filteredMedicalStaff={filteredMedicalStaff}
            searchInputValue={searchInputValue}
            selectedOrganizationId={selectedOrganizationId}
            days={days}
            shiftsData={shiftsData}
            setSearchInputValue={setSearchInputValue}
            setSelectedBlockStaffId={setSelectedBlockStaffId}
            setSelectedBlock={setSelectedBlock}
            handleBlockActionModalOpen={handleBlockActionModalOpen}
            handleStaffDetailsActionModalOpen={
              handleStaffDetailsActionModalOpen
            }
            setContainerRef={setContainerRef}
          />
        )}
      </div>

      {isBlockActionModalOpen && (
        <BlockActionModal
          isModalOpen={isBlockActionModalOpen}
          staffOptions={medicalStaffSelectOptions ?? []}
          selectedBlockStaffId={selectedBlockStaffId}
          selectedDay={selectedDay}
          startDate={startDate}
          modalMode={modalMode}
          selectedBlock={selectedBlock}
          selectedOrganizationId={selectedOrganizationId}
          onCloseModal={handleCloseBlockActionModal}
        />
      )}

      {isStaffDetailsActionModalOpen && (
        <StaffDetailsActionModal
          isModalOpen={isStaffDetailsActionModalOpen}
          staff={selectedStaffDetails}
          selectedDate={startDate}
          handleCloseModal={handleCloseStaffDetailsActionModal}
        />
      )}

      <ConfirmDialog
        open={isConfirmationModalOpen}
        confirmButtonDataTestId={
          StaffScheduleDataTestId.ConfirmPublishShiftsButton
        }
        setOpen={setIsConfirmationModalOpen}
        onConfirm={handlePublishShifts}
        width="lg"
        title="Czy chcesz opublikować zmiany?"
        text="Wprowadzone zmiany będą widoczne natychmiastowo w kalendarzach pacjentów."
        confirmText="Opublikuj"
      />
    </>
  );
};
